import {
  cabinetEndPanelFromLine,
  cabinetFromLine,
  caseworkOverrideName,
} from "building-generation/elements/caseworks/casework";
import type { Casework } from "building-types/casework/caseworkTypes";
import { PropInstanceHorizontalAlignment } from "building-types/datums/datumsTypesEnum";
import type { Prop } from "building-types/props/propTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import { PropCatalogType } from "building-types/settings/settingsTypesEnum";
import { WallType } from "building-types/walls/wallEnums";
import type { WallIndex } from "building-types/walls/wallIndex";
import type { WallStrip } from "building-types/walls/wallStrip";
import { CABINET_18, COUNTER_HEIGHT, WALL_THICKNESS_INTERIOR } from "common/constants/buildingObjects";
import { ensureDefined } from "common/utils/assertOfType";
import { degreeToRadian } from "common/utils/math";
import { BuiltInInteriorPropId } from "doc/elements/propCatalog/interiorPropCatalog";
import { defaultPropLabels } from "doc/propTools";
import { propOffsetFromLine2 } from "doc/rooms/propPlacement";
import {
  polyLine2Copy,
  polyLine2EdgeLength,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2Reverse,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { WallStripImpl } from "../walls/wallStripImpl";
import type { RoomLayoutGenerator } from "./roomLayout";
import { overloadIndex } from "./roomLayout";

const roomLayoutKitchen: RoomLayoutGenerator[] = [
  {
    roomType: RoomType.KITCHEN,
    async generate({ room, wallIndex, palette }) {
      const { strips, mainStrip, mainIndex } = kitchenWallStripIndices(room, wallIndex);
      if (!mainStrip) {
        return {};
      }

      const props: Prop[] = [];
      const casework: Casework[] = [];

      const solidWallLine = mainStrip.baseline(true);

      let prevRetreat = false;
      let nextRetreat = false;

      if (
        strips[overloadIndex(mainIndex - 1)]?.walls[strips[overloadIndex(mainIndex - 1)]?.startIndex].wallType ===
        WallType.SOLID
      ) {
        prevRetreat = true;
      }
      if (
        strips[overloadIndex(mainIndex + 1)]?.walls[strips[overloadIndex(mainIndex + 1)]?.startIndex].wallType ===
        WallType.SOLID
      ) {
        nextRetreat = true;
      }

      let mainLine;

      if (polyLine2EdgeLength(solidWallLine, 0) > 110) {
        mainLine = solidWallLine;
      }

      if (mainLine) {
        const mainLen = polyLine2EdgeLength(mainLine, 0);
        const rangeWidth = 32;

        // main lower
        casework.push(cabinetFromLine(room, mainLine, 36, 48));
        // end lower
        casework.push(cabinetFromLine(room, mainLine, mainLen - CABINET_18, 0));
        // end upper
        casework.push(cabinetFromLine(room, mainLine, mainLen - CABINET_18, 0, 0, true));
        // mid uppers
        casework.push(cabinetFromLine(room, mainLine, 36, rangeWidth + CABINET_18, 0, true, false));
        // range uppers
        casework.push(cabinetFromLine(room, mainLine, mainLen - (rangeWidth + CABINET_18), CABINET_18, 0, true, true));
        //fridge uppers
        casework.push(cabinetFromLine(room, mainLine, 0, mainLen - 36, 0, true, true, true, false));

        const range = await propOffsetFromLine2({
          settings: palette,
          propId: BuiltInInteriorPropId.OVEN,
          room,
          offset: 3.5,
          edge: mainLine,
          z: room.level.floorElevation,
          horizontalAlignment: PropInstanceHorizontalAlignment.LEFT,
          param: mainLen - 18,
          paramNormalized: false,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });
        props.push(range);

        const fridgeId = BuiltInInteriorPropId.REFRIGERATOR;

        const refrig = await propOffsetFromLine2({
          settings: palette,
          propId: fridgeId,
          room,
          offset: 1,
          edge: mainLine,
          z: room.level.floorElevation,
          horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
          param: 18,
          paramNormalized: false,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });

        // fridge surround
        casework.push(cabinetEndPanelFromLine(room, mainLine, 0));
        casework.push(cabinetEndPanelFromLine(room, mainLine, 36));
        props.push(refrig);

        const islandLine = polyLine2Copy(mainLine);

        if (prevRetreat) {
          polyLine2ExtendStart(islandLine, -36);
        }
        if (nextRetreat) {
          polyLine2ExtendEnd(islandLine, -36);
        }

        let islandLen = polyLine2EdgeLength(islandLine, 0);

        // island
        casework.push(
          caseworkOverrideName(cabinetFromLine(room, islandLine, 0, 0, 72, false, false, false, false), "island"),
        );

        islandLen = polyLine2EdgeLength(islandLine, 0);

        const sink = await propOffsetFromLine2({
          settings: palette,
          propId: BuiltInInteriorPropId.SINKDOUBLE,
          room,
          offset: 98,
          edge: islandLine,
          z: room.level.floorElevation + COUNTER_HEIGHT,
          horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
          param: 0.5,
          rotate: degreeToRadian(180),
          paramNormalized: true,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });
        props.push(sink);
        const dish = await propOffsetFromLine2({
          settings: palette,
          propId: BuiltInInteriorPropId.DISHWASHER,
          room,
          offset: 95,
          edge: islandLine,
          z: room.level.floorElevation,
          horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
          rotate: degreeToRadian(180),
          param: islandLen / 2 + 36,
          paramNormalized: false,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });
        props.push(dish);
      }

      return { props, casework };
    },
  },
  {
    roomType: RoomType.REFRIGERATOR,
    async generate({ room, wallIndex, palette }) {
      const voidWallGroups = wallIndex.wallStripsByPredicate(room, (wall) => wall.wallType === WallType.VOID, true);
      const voidWallLines = WallStripImpl.allEndPoints(voidWallGroups);

      voidWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));

      const hostLine = voidWallLines[0];
      polyLine2Reverse(hostLine);

      const props: Prop[] = [];

      const shelf = await propOffsetFromLine2({
        settings: palette,
        propId: BuiltInInteriorPropId.REFRIGERATORSIDE,
        room,
        offset: -26,
        edge: hostLine,
        z: room.level.floorElevation,
        horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
        catalogType: PropCatalogType.PROP_CATALOG,
        labels: defaultPropLabels(),
      });
      props.push(shelf);

      return {
        props,
      };
    },
  },
  {
    roomType: RoomType.KITCHEN_CABINETS,
    async generate({ room, wallIndex }) {
      const solidWallGroups = wallIndex.wallStripsByPredicate(room, (wall) => wall.wallType === WallType.SOLID, true);
      const solidWallLines = WallStripImpl.allEndPoints(solidWallGroups);
      solidWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));

      const hostLine = solidWallLines[0];
      // polyLine2Reverse(hostLine);

      const wallHalf = WALL_THICKNESS_INTERIOR / 2;
      const casework: Casework[] = [];
      casework.push(cabinetFromLine(room, hostLine, wallHalf, wallHalf, wallHalf, false));
      // casework.push(cabinetFromLine(room, hostLine, wallHalf, wallHalf, wallHalf, true));

      return {
        casework,
      };
    },
  },
];

export default roomLayoutKitchen;

function kitchenWallStripIndices(
  room: Room,
  wallIndex: WallIndex,
): { strips: WallStrip[]; mainIndex: number; mainStrip: WallStrip; solidWallLines: PolyLine2[] } {
  const strip0 = wallIndex.wallStripFromRoomEdgeIndex(room, 0);
  const strip1 = wallIndex.wallStripFromRoomEdgeIndex(room, 1);
  const strip2 = wallIndex.wallStripFromRoomEdgeIndex(room, 2);
  const strip3 = wallIndex.wallStripFromRoomEdgeIndex(room, 3);

  const strips: WallStrip[] = [strip0, strip1, strip2, strip3].filter((strip) => !!strip);

  // where are the solid walls?

  const solidWallLines: PolyLine2[] = [];

  for (const strip of strips) {
    if (strip && strip.walls[strip.startIndex].wallType === WallType.SOLID) {
      solidWallLines.push(strip.baseline(true));
    }
  }

  solidWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));

  let mainIndex = -1;
  let mainStrip: WallStrip | undefined;
  let found = false;

  for (let i = 0; i < strips.length; i++) {
    if (strips[i] && strips[i].walls[strips[i].startIndex].wallType === WallType.SOLID) {
      if (!found) {
        mainIndex = i;
        mainStrip = strips[i];
        found = true;
      }
      if (polyLine2EdgeLength(strips[i].baseline(true), 0) > polyLine2EdgeLength(strips[mainIndex].baseline(true), 0)) {
        mainIndex = i;
        mainStrip = strips[i];
      }
    }
  }

  if (mainIndex === -1) {
    mainIndex = 2;
  }

  return {
    strips,
    mainIndex,
    mainStrip: ensureDefined(mainStrip),
    solidWallLines,
  };
}
